import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { User, Settings, LogOut, Crown } from 'lucide-react';
import { useLocaleContext } from '../../context/LocaleContext';

interface UserMenuProps {
  userEmail: string;
  hasActiveSubscription: boolean;
  onLogoutClick: () => void;
}

const UserMenu: React.FC<UserMenuProps> = ({
  userEmail,
  hasActiveSubscription,
  onLogoutClick,
}) => {
  const { locale } = useLocaleContext();
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Get user initials from email
  const getInitials = (email: string): string => {
    const parts = email.split('@')[0].split('.');
    if (parts.length >= 2) {
      return (parts[0][0] + parts[1][0]).toUpperCase();
    }
    return email.substring(0, 2).toUpperCase();
  };

  const initials = getInitials(userEmail);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Close dropdown on Escape key
  useEffect(() => {
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) {
        setIsOpen(false);
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen]);

  const handleSubscriptionClick = () => {
    const subscriptionRoutes: Record<string, string> = {
      en: '/subscription',
      de: '/de/abo',
      'pt-BR': '/pt-br/assinatura',
      'ms-MY': '/my/langganan',
      es: '/es/suscripcion',
      fr: '/fr/abonnement',
      ja: '/ja/subscription',
      id: '/id/langganan',
    };
    navigate(subscriptionRoutes[locale] || '/subscription');
    setIsOpen(false);
  };

  const handleMyAccountClick = () => {
    // Navigate to account settings (use same route as subscription for now)
    handleSubscriptionClick();
  };

  return (
    <div className="relative" ref={dropdownRef}>
      {/* Avatar Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-center w-9 h-9 rounded-full bg-accent-600 dark:bg-accent-500 text-white font-semibold text-sm hover:bg-accent-700 dark:hover:bg-accent-600 transition-colors shadow-sm hover:shadow"
        aria-label="User menu"
        aria-expanded={isOpen}
        aria-haspopup="true"
      >
        {initials}
      </button>

      {/* Dropdown Menu */}
      {isOpen && (
        <div
          className="fixed sm:absolute right-4 sm:right-0 top-16 sm:top-auto sm:mt-2 w-64 max-w-[calc(100vw-2rem)] bg-white dark:bg-neutral-800 rounded-lg shadow-lg border border-neutral-200 dark:border-neutral-700 py-2 z-50 animate-fadeIn pointer-events-auto"
          role="menu"
        >
          {/* Email Header */}
          <div className="px-4 py-2 border-b border-neutral-200 dark:border-neutral-700">
            <div className="flex items-center gap-2">
              <User size={14} className="text-neutral-600 dark:text-neutral-400 flex-shrink-0" />
              <p className="text-sm text-neutral-900 dark:text-white font-medium truncate">
                {userEmail}
              </p>
            </div>
          </div>

          {/* Menu Items */}
          <div className="py-1">
            {/* My Account */}
            <button
              onClick={handleMyAccountClick}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
              role="menuitem"
            >
              <Settings size={16} className="flex-shrink-0" />
              <span>
                {locale === 'de'
                  ? 'Mein Konto'
                  : locale === 'fr'
                  ? 'Mon Compte'
                  : locale === 'es'
                  ? 'Mi Cuenta'
                  : locale === 'pt-BR'
                  ? 'Minha Conta'
                  : locale === 'ja'
                  ? 'アカウント'
                  : locale === 'id'
                  ? 'Akun Saya'
                  : 'My Account'}
              </span>
            </button>

            {/* Subscription with Premium Badge */}
            <button
              onClick={handleSubscriptionClick}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-neutral-700 dark:text-neutral-300 hover:bg-neutral-50 dark:hover:bg-neutral-700/50 transition-colors"
              role="menuitem"
            >
              <Crown
                size={16}
                className={`flex-shrink-0 ${
                  hasActiveSubscription
                    ? 'text-accent-600 dark:text-accent-400'
                    : ''
                }`}
              />
              <span className="flex-1 text-left">
                {locale === 'de'
                  ? 'Abonnement'
                  : locale === 'pt-BR'
                  ? 'Assinatura'
                  : locale === 'ms-MY'
                  ? 'Langganan'
                  : locale === 'es'
                  ? 'Suscripción'
                  : locale === 'fr'
                  ? 'Abonnement'
                  : locale === 'ja'
                  ? 'サブスクリプション'
                  : locale === 'id'
                  ? 'Langganan'
                  : 'Subscription'}
              </span>
              {hasActiveSubscription && (
                <span className="px-2 py-0.5 text-xs font-medium bg-accent-100 dark:bg-accent-900/30 text-accent-700 dark:text-accent-300 rounded">
                  Premium
                </span>
              )}
            </button>
          </div>

          {/* Sign Out - Separated */}
          <div className="border-t border-neutral-200 dark:border-neutral-700 py-1">
            <button
              onClick={() => {
                setIsOpen(false);
                onLogoutClick();
              }}
              className="w-full flex items-center gap-3 px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
              role="menuitem"
            >
              <LogOut size={16} className="flex-shrink-0" />
              <span>Sign Out</span>
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default React.memo(UserMenu);
